import React from 'react';
import { injectIntl } from 'react-intl';
import type { InjectedIntlProps } from 'react-intl';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { isAdditionalLogsForHelpObjectNotHydratedEnabled } from 'feature-flags';
import { graphql, useFragment } from 'react-relay';
import * as bp from 'view/styles/breakpoints';
import * as fonts from 'view/styles/fonts';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { AnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ThemedLink } from '@atlassian/help-center-common-component/themed-link';
import { HelpObject } from '../help-object';
import type { topicFragment$key as TopicFragmentType } from './__generated__/topicFragment.graphql';
import messages from './messages';

interface Props {
    topic: TopicFragmentType;
    disableAllLinks?: boolean;
}

const Topic = (props: Props & InjectedIntlProps & WithAnalyticsEventsProps) => {
    const { name, items, topicId } = useFragment<TopicFragmentType>(
        graphql`
            fragment topicFragment on HelpCenterTopic {
                name
                topicId
                items {
                    edges {
                        ...helpObject
                        node {
                            ari
                            data {
                                __typename
                            }
                        }
                    }
                }
            }
        `,
        props.topic
    );
    const MAX_HELP_OBJECTS = 3;

    const allHelpObjects = items?.edges || [];

    if (isAdditionalLogsForHelpObjectNotHydratedEnabled()) {
        allHelpObjects.forEach((o) => {
            if (!o?.node?.data) {
                const { createAnalyticsEvent } = props;

                if (createAnalyticsEvent) {
                    const analyticsEvent = createAnalyticsEvent({
                        analyticsType: OPERATIONAL_EVENT_TYPE,
                        action: 'invalidHelpObjectFound',
                        actionSubject: 'helpObject',
                    });
                    analyticsEvent.fire();
                }
            }
        });
    }

    const visibleHelpObjects = allHelpObjects.filter((o) => o?.node?.data?.__typename !== 'HelpObjectStoreQueryError');

    const showMoreCount = visibleHelpObjects.length - MAX_HELP_OBJECTS;

    const countMessage = props.intl.formatMessage(messages.showMoreText, {
        count: showMoreCount,
    });

    if (visibleHelpObjects.length) {
        return (
            <Container>
                <TopicName
                    to={`/topic/${topicId}`}
                    actionSubjectId={'topicTitle'}
                    disabled={Boolean(props.disableAllLinks)}
                    tabIndex={props.disableAllLinks ? -1 : undefined}
                >
                    {name}
                </TopicName>
                <HelpObjects>
                    {visibleHelpObjects.slice(0, MAX_HELP_OBJECTS).map((helpObject, index) => (
                        <ListItem key={helpObject?.node?.ari}>
                            <AnalyticsContext data={{ attributes: { helpObjectPosition: index + 1 } }}>
                                {helpObject && (
                                    <HelpObject helpObject={helpObject} disableAllLinks={props.disableAllLinks} />
                                )}
                            </AnalyticsContext>
                        </ListItem>
                    ))}
                </HelpObjects>
                {showMoreCount > 0 && (
                    <ShowMoreLink
                        to={`/topic/${topicId}`}
                        actionSubjectId={'showMoreHelpObjects'}
                        disabled={Boolean(props.disableAllLinks)}
                        tabIndex={props.disableAllLinks ? -1 : undefined}
                    >
                        {countMessage}
                    </ShowMoreLink>
                )}
            </Container>
        );
    }
    return null;
};

const TopicWithoutAnalyticsEvents = injectIntl(Topic);
export { TopicWithoutAnalyticsEvents };

export default withAnalyticsEvents()(injectIntl(Topic));

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const HelpObjects = styled.ol`
    ${fonts.xsmall};
    padding: ${token('space.0', '0px')};
    list-style-type: none;
    display: flex;
    flex-direction: column;
    width: 100%;
    ${bp.fromXSmall.css`
        ${fonts.regular};
    `};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TopicName = styled<React.ComponentProps<typeof ThemedLink> & { disabled?: boolean }>(ThemedLink)`
    ${fonts.h400};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    ${bp.fromXSmall.css`
        ${fonts.h500};
    `};
    pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    height: 100%;
    & ${HelpObjects} {
        margin: ${token('space.100', '8px')} ${token('space.0', '0px')};
    }
    & ${TopicName} {
        margin-top: ${token('space.0', '0px')};
    }
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ShowMoreLink = styled<React.ComponentProps<typeof ThemedLink> & { disabled?: boolean }>(ThemedLink)`
    margin-top: auto;
    pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ListItem = styled.li`
    margin-top: ${token('space.0', '0px')};
`;
